import { useMemo } from 'react';
import {
  canExportData,
  canImportData,
  canSendBroadcast,
  canManageSettings,
  canViewAudit,
  UserRole,
} from '@cls/shared';
import { useAuth } from './AuthProvider.js';

/**
 * Central hook for role-gated UI actions. Returns boolean flags the page
 * components use to hide buttons the current role shouldn't see.
 *
 * Roles (post 2026-05 consolidation):
 *   - Admin   — full access, the only role that can edit/create/delete.
 *   - Manager — read-only everywhere except Users + Settings; can press
 *               WhatsApp / email / SMS buttons and trigger broadcasts.
 *   - Worker  — self-service only (own profile, own deployments, own certs,
 *               own calendar, sign-in to current site, doc-sign, availability
 *               buttons). No edit affordances anywhere.
 *
 * All flags default to `false` when there's no user yet (loading state).
 */
export function useRolePerms() {
  const { user } = useAuth();

  return useMemo(() => {
    if (!user) {
      return {
        isAdmin: false,
        isManager: false,
        isSupervisor: false,
        isWorker: false,
        isSiteUser: false,
        isInternal: false,
        canImport: false,
        canExport: false,
        canBroadcast: false,
        canManageSettings: false,
        canViewAudit: false,
        canEditProjects: false,
        canEditCustomers: false,
        canEditContacts: false,
        canEditSuppliers: false,
        canEditWorkers: false,
        canCreateVacancy: false,
        canDeploy: false,
        canApproveTimesheet: false,
        canUseComms: false,
      };
    }
    const r = user.role;
    const isAdmin = r === UserRole.Admin;
    const isManager = r === UserRole.Manager;
    const isSupervisor = r === UserRole.Supervisor;
    const isWorker = r === UserRole.Worker;
    return {
      isAdmin,
      isManager,
      isSupervisor,
      isWorker,
      isSiteUser: isWorker || isSupervisor,
      // Kept for callers that already check `isInternal` to mean "admin-level UI".
      // Only Admin counts as internal under the new model.
      isInternal: isAdmin,
      canImport: canImportData(user),
      canExport: canExportData(user),
      canBroadcast: canSendBroadcast(user),
      canManageSettings: canManageSettings(user),
      canViewAudit: canViewAudit(user),
      canEditProjects: isAdmin,
      canEditCustomers: isAdmin,
      canEditContacts: isAdmin,
      canEditSuppliers: isAdmin,
      canEditWorkers: isAdmin,
      canCreateVacancy: isAdmin,
      canDeploy: isAdmin,
      canApproveTimesheet: isAdmin,
      // Manager + Admin can press the WA / email / SMS comms buttons. Workers
      // cannot (they aren't sending messages to other workers).
      canUseComms: isAdmin || isManager,
    };
  }, [user]);
}
